import { PlusIcon, XIcon } from "lucide-react";

import type { ShopSlotItem } from "./types";

import { Column } from "@/components/Column";
import { Text } from "@/components/Text";
import { Button } from "@ui/button";

import { cn } from "@/lib/utils";

export const ShopGearSlot = ({
  className,
  cost,
  isActive,
  isChanged,
  item,
  label,
  onClear,
  onSelect,
}: {
  className?: string;
  // Coins still owed for the item in this slot; 0 once owned.
  cost: number;
  isActive: boolean;
  isChanged: boolean;
  item: ShopSlotItem | null;
  label: string;
  onClear?: () => void;
  onSelect: () => void;
}) => {
  return (
    <Column className={cn("relative items-center gap-1", className)}>
      <Text className="font-mono text-[8px] font-black uppercase tracking-[0.12em] text-slate-500">
        {label}
      </Text>
      <Button
        type="button"
        variant="outlineDark"
        onClick={onSelect}
        className={cn(
          "relative h-16 w-16 overflow-hidden rounded-none border-2 border-slate-900 p-1 shadow-[2px_2px_0_#172033]",
          item ? "bg-white" : "border-dashed bg-white/60 text-slate-400",
          isActive &&
            "bg-orange-100 outline outline-[3px] outline-offset-2 outline-orange-500",
        )}
      >
        {item ? item.art : <PlusIcon className="h-5 w-5" />}
      </Button>
      {onClear && item ? (
        <Button
          type="button"
          variant="outlineDark"
          onClick={onClear}
          className="absolute -left-1 top-3 h-5 w-5 rounded-none border-2 border-slate-900 bg-white p-0 text-slate-700 hover:bg-orange-100"
        >
          <XIcon className="h-3 w-3" />
          <Text className="sr-only">{`Remove ${item.label}`}</Text>
        </Button>
      ) : null}
      <ShopGearSlotBadge cost={cost} isChanged={isChanged} />
      {/* Fixed two-line height so long names don't resize the sheet's grid rows. */}
      <Text
        className={cn(
          "line-clamp-2 h-[2.5em] max-w-[76px] text-center font-mono text-[9px] font-black uppercase leading-tight",
          item ? "text-slate-900" : "text-slate-400",
        )}
      >
        {item?.label ?? "Empty"}
      </Text>
    </Column>
  );
};

// Coins owed wins over the plain "changed" dot.
const ShopGearSlotBadge = ({
  cost,
  isChanged,
}: {
  cost: number;
  isChanged: boolean;
}) => {
  /* Render */
  const render = () => {
    if (cost > 0) {
      return (
        <Text className="absolute -right-2 top-2 border-2 border-slate-900 bg-amber-300 px-1 font-mono text-[8px] font-black text-slate-900">
          {cost.toString()}
        </Text>
      );
    }
    if (isChanged) {
      return (
        <Column className="absolute -right-1 top-3 h-3 w-3 border-2 border-slate-900 bg-orange-500" />
      );
    }
    return null;
  };

  return render();
};
